import { createStore, StoreApi } from 'zustand';
import { Point } from '../../../shared/types/mathTypes.ts';
import { Marker, MeasurementPlan, Position } from '../../../shared/types/protocol.ts';
import { OptionalExcept } from '../../../shared/types/utilTypes.ts';
import { maxOf, removeElementByIndex } from '../../../shared/utils/arrayUtils.ts';
import { partDisplayName } from '../../../shared/utils/partDisplayName.ts';
import { onGeneralToleranceChanged, onPositionFieldChanged } from '../../components/protocol-form/useRecalcTolerances.ts';
import { PositionSelection } from '../../types/positionSelection.ts';
import { Protocol } from '../../types/sharedTypeImpl.ts';
import { addMeasurementPlanPos, removeMeasurementPlanPos } from '../../utils/editMeasurementPlan.ts';
import { PositionOrdering, sortPositions } from './sortPositions.ts';


export type ProtocolStore = StoreApi<ProtocolState>;

export interface ProtocolState {
    protocol: Protocol | null;
    isDirty: boolean;
    positionSelection: PositionSelection | null;
    setProtocol: (protocol: Protocol | null) => void;
    setIsDirty: (isDirty: boolean) => void;
    setPositionSelection: (positionSelection: PositionSelection | null) => void;
    setProtocolField: (field: keyof Protocol, value: string | string[] | number | null) => void;
    deleteProtocolField: (field: keyof Protocol) => void;
    setPositionField: (index: number, field: keyof Position, value: string | number | boolean) => void;
    setMarkerField: (positionIdx: number, markerIdx: number, field: keyof Marker, value: string | number) => void;
    setMarkerLocation: (positionSelection: PositionSelection, page: number, pdfCoords: Point, pdfCoords2?: Point) => void;
    addPosition: (position: OptionalExcept<Position, 'type'>, page: number, pdfCoords: Point, pdfCoords2?: Point) => void;
    addMarker: (positionSelection: PositionSelection, pdfCoords: Point, pdfCoords2?: Point) => void;
    removePosition: (positionIdx: number) => void;
    removeMarker: (positionIdx: number, markerIdx: number) => void;
    reorderPositions: (ordering: PositionOrdering) => void;
    submitMeasurementPlan: (measurementPlan: MeasurementPlan, measureInterval: number | null) => void;
}

export const createProtocolStore = () =>
    createStore<ProtocolState>((set, get) => ({
        protocol: null,
        isDirty: false,
        positionSelection: null,

        setProtocol: (protocol) => set((state) => ({ ...state, protocol, })),

        setIsDirty: (isDirty) => set((state) => ({ ...state, isDirty, })),

        setPositionSelection: (positionSelection) => set((state) => ({ ...state, positionSelection, })),

        setProtocolField: (field, value) => set((state) => {
            if (!state.protocol) return state;
            const protocol = { ...state.protocol, [field]: value };
            if (field === 'generalTolerance') {
                onGeneralToleranceChanged(protocol);
            }
            if (field === 'partName' || field === 'partRevision') {
                protocol.partDisplayName = partDisplayName(protocol.partName, protocol.partRevision);
            }
            return {
                ...state,
                protocol,
                isDirty: true,
            };
        }),

        deleteProtocolField: (field) => set((state) => {
            if (!state.protocol) return state;
            const protocol = { ...state.protocol };
            delete protocol[field];
            return {
                ...state,
                protocol,
                isDirty: true
            };
        }),

        setPositionField: (index, field, value) => set((state) => {
            if (!state.protocol) return state;
            const newPosition = {
                ...state.protocol.positions[index],
                [field]: value,
            };
            onPositionFieldChanged(newPosition, field, state.protocol.generalTolerance ?? '');
            const protocol = {
                ...state.protocol,
                positions: state.protocol.positions.with(index, newPosition),
            };
            return {
                ...state,
                protocol,
                isDirty: true,
            };
        }),

        setMarkerField: (positionIdx, markerIdx, field, value) => set((state) => {
            if (!state.protocol) return state;
            const position = state.protocol.positions[positionIdx];
            const newMarker = {
                ...position.markers[markerIdx],
                [field]: value,
            };
            const protocol = {
                ...state.protocol,
                positions: state.protocol.positions.with(positionIdx, {
                    ...position,
                    markers: position.markers.with(markerIdx, newMarker),
                }),
            };
            return {
                ...state,
                protocol,
                isDirty: true,
            };
        }),

        setMarkerLocation: (positionSelection, page, pdfCoords, pdfCoords2) => set((state) => {
            if (!state.protocol) return state;
            if (state.protocol.positions[positionSelection.positionIdx].type === 'generalInspection') return state;
            const positions = [...state.protocol.positions];
            const position = positions[positionSelection.positionIdx];
            positions[positionSelection.positionIdx].page = page;

            const markers = position.markers;
            const marker = markers[positionSelection.markerIdx ?? 0];
            marker.x = pdfCoords.x;
            marker.y = pdfCoords.y;
            if (pdfCoords2) {
                marker.x2 = pdfCoords2.x;
                marker.y2 = pdfCoords2.y;
            } else {
                delete marker.x2;
                delete marker.y2;
            }
            const protocol = {
                ...state.protocol,
                positions,
            };
            return {
                ...state,
                protocol,
                isDirty: true,
            };
        }),

        addPosition: (position, page, pdfCoords, pdfCoords2) => set((state) => {
            if (!state.protocol) return state;

            const maxId = maxOf(state.protocol.positions ?? [], (it: Position) => it.id);
            const nextId = maxId ? Math.floor(maxId) + 1 : 1;
            const maxPosNum = maxOf(state.protocol.positions ?? [], (it: Position) => +it.positionNumber);
            const nextPosNum = maxPosNum ? Math.floor(maxPosNum) + 1 : 1;

            const newPosition: Position = {
                ...position,
                id: nextId,
                positionNumber: nextPosNum.toString(),
                page,
                markers: [{
                    x: pdfCoords.x,
                    y: pdfCoords.y,
                    ...(pdfCoords2 && { x2: pdfCoords2?.x }),
                    ...(pdfCoords2 && { y2: pdfCoords2?.y }),
                }],
            };
            const measurementPlan = state.protocol.measurementPlan
                && addMeasurementPlanPos(state.protocol.measurementPlan, newPosition);

            const positions = [...state.protocol.positions ?? [], newPosition];
            const protocol = {
                ...state.protocol,
                positions,
                ...(measurementPlan && { measurementPlan, requiresPlanReview: true }),
            };
            return {
                ...state,
                protocol,
                isDirty: true,
                positionSelection: { positionIdx: positions.length - 1 },
            };
        }),

        addMarker: (positionSelection, pdfCoords, pdfCoords2) => set((state) => {
            if (!state.protocol) return state;
            if (state.protocol.positions[positionSelection.positionIdx].type === 'generalInspection') return state;

            const positions = [...state.protocol.positions];
            const position = positions[positionSelection.positionIdx];
            const markers = position.markers;
            const newMarker: Marker = {
                x: pdfCoords.x,
                y: pdfCoords.y,
                ...(pdfCoords2 && { x2: pdfCoords2?.x }),
                ...(pdfCoords2 && { y2: pdfCoords2?.y }),
            };
            markers.push(newMarker);

            const protocol = {
                ...state.protocol,
                positions,
            };
            return {
                ...state,
                protocol,
                isDirty: true,
            };
        }),

        removePosition: (positionIdx) => set((state) => {
            if (!state.protocol) return state;

            const removedPosition = state.protocol.positions[positionIdx];
            const positions = removeElementByIndex(state.protocol.positions, positionIdx);
            const measurementPlan = state.protocol.measurementPlan
                && removeMeasurementPlanPos(state.protocol.measurementPlan, removedPosition.id);

            const protocol = {
                ...state.protocol,
                positions,
                ...(measurementPlan && { measurementPlan }),
            };
            return {
                ...state,
                protocol,
                positionSelection: null,
                isDirty: true,
            };
        }),

        removeMarker: (positionIdx, markerIdx) => set((state) => {
            if (!state.protocol) return state;

            const positions = [...state.protocol.positions];
            const position = positions[positionIdx];
            const markers = position.markers;
            markers.splice(markerIdx, 1);
            if (markers.length === 0) {
                positions.splice(positionIdx, 1);
            }
            const protocol = {
                ...state.protocol,
                positions,
            };
            return {
                ...state,
                protocol,
                positionSelection: { positionIdx },
                isDirty: true,
            };
        }),

        reorderPositions: (ordering: PositionOrdering) => set((state) => {
            if (!state.protocol) return state;

            const positions = sortPositions([...state.protocol.positions], ordering);
            const protocol = {
                ...state.protocol,
                positions,
            };
            return {
                ...state,
                protocol,
                positionSelection: null,
                isDirty: true,
            };
        }),

        submitMeasurementPlan: (measurementPlan, measureInterval) => set((state) => {
            if (!state.protocol) return state;
            const protocol = {
                ...state.protocol,
                measurementPlan: { ...measurementPlan },
                measureInterval: measureInterval ?? undefined,
            };
            return {
                ...state,
                protocol,
                isDirty: true,
            };
        }),
    }));
